import React, { useEffect, useState } from 'react';
import * as Yup from "yup";
import Breadcrumbs from '../components/BreadCrumbs';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import Button from '../components/buttons/Button';
import InputField from '../components/inputFields/InputField';
import AsyncFormikDropdown from '../components/inputFields/AsyncFormikDropDown';
import { patchFarmer, postFarmer } from '../services/farmers';
import { searchUser } from '../services/search';
import { callPincodeApi, applyPincodeToForm } from '../services/pincode';
import { sanitizeMobile } from '../utils/phone';
import { BsExclamationTriangleFill } from 'react-icons/bs';

const emptyFormValues = {
    user_search: "",
    first_name: "",
    last_name: "",
    mobile_number: "",
    shop_name: "",
    gstin: "",
    line1: "",
    line2: "",
    landmark: "",
    city: "",
    district: "",
    state: "",
    country: "",
    zipcode: "",
};

/** Map API user payload into form field values.
 *  Handles both search API shape (name / phone_number) and
 *  POST-exists / direct-edit shape (first_name / last_name / mobile_number).
 */
const mapUserToFormValues = (user = {}) => {
    const addr = user.address || {};
    const fullName = user.name ?? "";
    const spaceIdx = fullName.indexOf(" ");
    const derivedFirst = spaceIdx >= 0 ? fullName.slice(0, spaceIdx) : fullName;
    const derivedLast  = spaceIdx >= 0 ? fullName.slice(spaceIdx + 1) : "";
    return {
        user_search: "",
        first_name: user.first_name ?? derivedFirst,
        last_name:  user.last_name  ?? derivedLast,
        mobile_number: user.mobile_number ?? user.phone_number ?? "",
        shop_name: user.shop_name ?? "",
        gstin: user.gstin ?? "",
        line1:     addr.line1     ?? "",
        line2:     addr.line2     ?? "",
        landmark:  addr.landmark  ?? "",
        city:      addr.city      ?? "",
        district:  addr.district  ?? "",
        state:     addr.state     ?? "",
        country:   addr.country   ?? "",
        zipcode:   addr.zipcode   ?? addr.pincode ?? "",
    };
};

const buildAddressPayload = (values) => ({
    line1:    values.line1,
    line2:    values.line2,
    landmark: values.landmark,
    city:     values.city,
    district: values.district,
    state:    values.state,
    country:  values.country,
    zipcode:  values.zipcode,
});

function AddFarmer() {
    const location = useLocation();
    const navigate = useNavigate();
    const [existingUserId, setExistingUserId] = useState(null);
    const [formInitialValues, setFormInitialValues] = useState(emptyFormValues);
    const [userOptions, setUserOptions] = useState([]);

    useEffect(() => {
        const stateUser = location.state?.user ?? location.state;
        if (stateUser?.id) {
            setExistingUserId(stateUser.id);
            setFormInitialValues(mapUserToFormValues(stateUser));
        }
    }, [location.state]);

    const handleDiscardChanges = (resetForm) => {
        setExistingUserId(null);
        resetForm({ values: emptyFormValues });
        navigate(-1);
    };

    const handleCreateUser = async (values, { setSubmitting, setValues }) => {
        const data = {
            first_name: values.first_name,
            last_name:  values.last_name,
            mobile_number: values.mobile_number,
            shop_name: values.shop_name || undefined,
            gstin:     values.gstin     || undefined,
            address:   buildAddressPayload(values),
        };

        try {
            const response = await postFarmer(data);
            const payload = response?.data;

            if (payload?.exists && payload?.user) {
                const mapped = mapUserToFormValues(payload.user);
                setExistingUserId(payload.user.id);
                setValues(mapped);
                toast.warning(
                    "This mobile number is already registered. Details have been loaded — you can update them below.",
                    { autoClose: 6000 }
                );
                return;
            }

            if (response?.status === 201 || payload?.user_id) {
                toast.success("User added successfully!");
                navigate(-1);
                return;
            }

            toast.error("Something went wrong. Please try again.");
        } catch (error) {
            const errors =
                error?.response?.data?.error ||
                error?.response?.data?.detail ||
                "Something went wrong. Please try again.";
            toast.error(errors);
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateUser = async (values, { setSubmitting }) => {
        if (!existingUserId) return;

        const data = {
            id:         existingUserId,
            first_name: values.first_name,
            last_name:  values.last_name,
            shop_name:  values.shop_name || undefined,
            gstin:      values.gstin     || undefined,
            address:    buildAddressPayload(values),
        };

        try {
            const response = await patchFarmer(data);

            if (response?.status === 200 || response?.status === 204) {
                toast.success("User updated successfully!");
                navigate(-1);
                return;
            }

            toast.error("Something went wrong. Please try again.");
        } catch (error) {
            const errors =
                error?.response?.data?.error ||
                error?.response?.data?.detail ||
                "Something went wrong. Please try again.";
            toast.error(errors);
        } finally {
            setSubmitting(false);
        }
    };

    const handleFormSubmit = async (values, formikBag) => {
        if (existingUserId) {
            await handleUpdateUser(values, formikBag);
        } else {
            await handleCreateUser(values, formikBag);
        }
    };

    const clearExistingUser = (resetForm) => {
        setExistingUserId(null);
        resetForm({ values: emptyFormValues });
    };

    const isUpdateMode = Boolean(existingUserId);

    return (
        <>
            <Breadcrumbs
                items={[
                    { name: 'Home', link: '/dashboard' },
                    { name: 'Farmers | Users', link: '/farmers' },
                    { name: isUpdateMode ? 'Update User' : 'Add User' },
                ]}
            />
            <div className="mt-8">
                <Formik
                    initialValues={formInitialValues}
                    enableReinitialize
                    validationSchema={Yup.object({
                        first_name:    Yup.string().required("First name is required"),
                        mobile_number: Yup.string().required("Mobile is required"),
                        zipcode:       Yup.string().required("Provide Zipcode"),
                    })}
                    onSubmit={handleFormSubmit}
                >
                    {({ isSubmitting, setFieldValue, resetForm, values, setValues }) => (
                        <Form
                            onKeyDown={(e) => {
                                if (e.key === "Enter") e.preventDefault();
                            }}
                        >
                            {/* Search existing user */}
                            <div className="mb-4">
                                <AsyncFormikDropdown
                                    label="Search Existing User"
                                    name="user_search"
                                    placeholder="Type name or mobile to find and load an existing user…"
                                    needSearch
                                    apiCallFunction={async (input) => {
                                        const response = await searchUser(input);
                                        setUserOptions(response.data);
                                        return response.data.map((u) => ({
                                            value: u.id,
                                            label: `${u.name} | ${u.phone_number}${u.address?.state ? " | " + u.address.state : ""}`,
                                        }));
                                    }}
                                    onChange={(selectedOption) => {
                                        if (!selectedOption) return;
                                        const user = userOptions.find((u) => u.id === selectedOption.value);
                                        if (!user) return;
                                        setExistingUserId(user.id);
                                        setValues(mapUserToFormValues(user));
                                    }}
                                />
                            </div>

                            {isUpdateMode && (
                                <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 flex gap-3 items-start">
                                    <BsExclamationTriangleFill className="text-amber-500 shrink-0 mt-0.5" size={20} />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold">User already exists</p>
                                        <p className="text-sm mt-1 text-amber-900/90">
                                            A user with mobile <span className="font-medium">{values.mobile_number}</span> is already registered.
                                            Their details are shown below — edit what you need and click <strong>Update User</strong>.
                                            Mobile number cannot be changed.
                                        </p>
                                        <button
                                            type="button"
                                            className="text-sm text-primary font-medium mt-2 hover:underline"
                                            onClick={() => clearExistingUser(resetForm)}
                                        >
                                            Use a different mobile number
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="border border-gray-300 mt-5 p-4 rounded-lg bg-gray-100 flex flex-col md:flex-row gap-4">
                                <div className="flex flex-col w-full">
                                    <div className="grid gap-2 grid-cols-1 md:grid-cols-3">
                                        <InputField
                                            label="First Name"
                                            name="first_name"
                                            type="text"
                                            placeholder="First Name"
                                            required
                                        />
                                        <InputField
                                            label="Last Name"
                                            name="last_name"
                                            type="text"
                                            placeholder="Last Name"
                                        />
                                        <InputField
                                            label="Mobile"
                                            name="mobile_number"
                                            type="text"
                                            placeholder="XXXXXXXXX"
                                            required
                                            disable={isUpdateMode}
                                            onChange={(e) => setFieldValue('mobile_number', sanitizeMobile(e.target.value))}
                                        />
                                        <InputField
                                            label="Shop Name"
                                            name="shop_name"
                                            type="text"
                                            placeholder="Shop Name (Optional)"
                                        />
                                        <InputField
                                            label="GSTIN"
                                            name="gstin"
                                            type="text"
                                            placeholder="GSTIN (Optional)"
                                        />
                                    </div>
                                    <div className="grid gap-2 grid-cols-1 md:grid-cols-3">
                                        <InputField
                                            label="Line 1"
                                            name="line1"
                                            type="text"
                                            placeholder="Enter Address 1"
                                        />
                                        <InputField
                                            label="Line 2"
                                            name="line2"
                                            type="text"
                                            placeholder="Enter Address 2 (Optional)"
                                        />
                                        <InputField
                                            label="Landmark"
                                            name="landmark"
                                            type="text"
                                            placeholder="Enter Landmark (Optional)"
                                        />
                                        <InputField
                                            label="Zipcode"
                                            name="zipcode"
                                            type="text"
                                            placeholder="452001"
                                            onChange={async (value) => {
                                                setFieldValue("zipcode", value.target.value);
                                                if (value.target.value.length === 6) {
                                                    const resp = await callPincodeApi({ value: value.target.value });
                                                    applyPincodeToForm(setFieldValue, resp);
                                                }
                                            }}
                                            required
                                        />
                                        <InputField
                                            label="City"
                                            name="city"
                                            type="text"
                                            placeholder="City"
                                        />
                                        <InputField
                                            label="District"
                                            name="district"
                                            type="text"
                                            placeholder="District"
                                        />
                                        <InputField
                                            label="State"
                                            name="state"
                                            type="text"
                                            placeholder="State"
                                        />
                                        <InputField
                                            label="Country"
                                            name="country"
                                            type="text"
                                            placeholder="Country"
                                        />
                                    </div>
                                    <div className="py-5 flex justify-end gap-6">
                                        <div>
                                            <Button
                                                type="button"
                                                className="text-primary bg-white hover:bg-white-dark font-sm rounded-lg text-lg w-full px-8 py-1.5 text-center border border-primary"
                                                onClick={() => handleDiscardChanges(resetForm)}
                                                disabled={isSubmitting}
                                            >
                                                Discard Changes
                                            </Button>
                                        </div>
                                        <div>
                                            <Button
                                                type="submit"
                                                className="text-white bg-primary hover:bg-primary-dark font-sm rounded-lg text-lg w-full px-8 py-1.5 text-center"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting
                                                    ? isUpdateMode ? "Updating..." : "Saving..."
                                                    : isUpdateMode ? "Update User" : "Save"}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    );
}

export default AddFarmer;
